<template>
  <div class="modal-content">
    <div class="modal-header">
      <h4 class="modal-title">{{ form.name || _('Form') }}</h4>
    </div>
    <div class="modal-body">
      <div class="row">
        <div class="col-md-12">
          <div class="form-group languageversioned">
            <label for="name">{{ _('Name') }}</label><br />
            <input type="text" class="form-control" v-model="form.name" />
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-md-12">
          <h4 class="languageversioned">{{ _('Fields') }}</h4>
          <p v-if="isTranslation" class="text-muted">{{ _('You are translating the fields. Add, remove and reorder fields in the default language.') }}</p>
          <p v-if="translationAligned" class="text-warning">{{ _('The fields were changed in the default language. Check the translations and save.') }}</p>
          <p v-if="structureChanged" class="text-warning">{{ _('Fields were added, removed or reordered. Open and save the form in each other language, otherwise those languages keep showing the old fields.') }}</p>
        </div>
      </div>
      <div class="row">
        <template v-for="(field, index) in fields" :key="index">
          <div class="col-md-9">
            <div :class="fieldClass(field)">
              <label v-if="!isCheckbox(field) && !field.condition">{{ field.title }} <template v-if="parseInt(field.mandatory)">*</template></label>
              <input class="form-control" v-if="isInputField(field)" :type="field.type" :disabled="!!field.disabled" v-model="data[field.name]" :placeholder="field.placeholder" :lang="field.lang" :ref="'dlg_' + field.name" />
              <div v-if="isCheckbox(field)" class="checkbox">
                <label v-if="field.title">
                  <input :type="field.type" v-model="data[field.name]" :lang="field.lang" true-value="1" false-value="0" />
                  <span v-html="field.title"></span>
                </label>
              </div>
              <textarea class="form-control" v-if="isTextArea(field)" wrap="virtual" rows="4" v-model="data[field.name]" :placeholder="field.placeholder" :lang="field.lang" :ref="'dlg_' + field.name"></textarea>
              <p v-if="isStaticText(field)" v-html="field.text"></p>
              <h3 v-if="isH3(field)" v-html="field.text"></h3>
              <select class="form-control" v-if="isSelectField(field)" v-model="data[field.name]" :placeholder="field.placeholder">
                <option v-for="option in parseOptions(field.options)" :value="option.value" :disabled="option.disabled" v-html="option.name"></option>
              </select>
            </div>
          </div>
          <div class="col-md-3">
            <template v-if="!isCheckbox(field) && !field.condition"><label>&nbsp;</label><br /></template>
            <div class="btn-group">
              <button class="btn btn-xs btn-success" v-on:click="editFieldDialog(index)"><i class="fa fa-fw fa-pencil"></i></button>
              <template v-if="!isTranslation">
                <button class="btn btn-xs btn-danger" v-on:click="removeField(index)"><i class="fa fa-fw fa-trash"></i></button>
                <button class="btn btn-xs btn-default" v-on:click="moveFieldUp(index)" :disabled="index === 0"><i class="fa fa-fw fa-arrow-up"></i></button>
                <button class="btn btn-xs btn-default" v-on:click="moveFieldDown(index)" :disabled="index === fields.length - 1"><i class="fa fa-fw fa-arrow-down"></i></button>
              </template>
            </div>
          </div>
        </template>
      </div>
      <div class="row" v-if="!isTranslation">
        <div class="col-md-3">
          <Dropdown buttonClass="btn-success" :teleport="true">
            <template #trigger>
              <i class="fa fa-fw fa-plus"></i> {{ _('Add Field') }} &nbsp;<span class="caret"></span>
            </template>
            <li><a href="javascript:;" v-on:click="addField('text')"><i class="fa fa-fw fa-input-text"></i> {{ _('Text field') }}</a></li>
            <li><a href="javascript:;" v-on:click="addField('textarea')"><i class="fa fa-fw fa-input-pipe"></i> {{ _('Text area') }}</a></li>
            <li><a href="javascript:;" v-on:click="addField('checkbox')"><i class="fa fa-fw fa-square-check"></i> {{ _('Checkbox') }}</a></li>
            <li><a href="javascript:;" v-on:click="addField('select')"><i class="fa fa-fw fa-list-radio"></i> {{ _('Select list') }}</a></li>
            <hr />
            <li><a href="javascript:;" v-on:click="addField('static')"><i class="fa fa-fw fa-text"></i> {{ _('Static text') }}</a></li>
            <li><a href="javascript:;" v-on:click="addField('h3')"><i class="fa fa-fw fa-heading"></i> {{ _('Heading') }}</a></li>
          </Dropdown>
        </div>
      </div>
    </div>
    <div class="modal-footer">
      <button v-if="form.id" class="btn btn-danger pull-left" v-on:click="deleteFormDialog"><i class="fa fa-fw fa-trash"></i> {{ _('Delete') }}</button>
      <button class="btn btn-default" v-on:click="hideModal"><i class="fa fa-fw fa-close"></i> {{ _('Close') }}</button>
      <button class="btn btn-success" v-on:click="saveForm"><i class="fa fa-fw fa-save"></i> {{ _('Save') }}</button>
    </div>
  </div>
</template>

<script>
import modals from '~/lib/modals'
import axios from 'axios'
import { mapGetters } from 'vuex'

export default {
  mixins: [modals],
  data () {
    return {
      form: { ...this.sourceForm }, // Make a copy of the form so we can make changes
      fields: [],
      data: {},
      loadedStructure: null,
      translationAligned: false
    }
  },
  props: ['sourceForm'],
  computed: {
    ...mapGetters(['allSettings', 'apiKey', 'baseDomain', 'locale', 'shopId', 'version']),
    shopSettings () {
      return this.allSettings ? this.allSettings[this.shopId + '-' + this.locale] : null
    },
    defaultVersion () {
      return this.shopSettings ? this.shopSettings.defaultversion : null
    },
    hasOtherVersions () {
      return !!this.shopSettings && (this.shopSettings.versions || '').split(';').filter(v => v).length > 1
    },
    isTranslation () {
      return !!this.form.id && !!this.version && !!this.defaultVersion && this.version !== this.defaultVersion
    },
    structureChanged () {
      return !this.isTranslation && this.hasOtherVersions && !!this.form.id && this.loadedStructure !== null && this.loadedStructure !== this.structureOf(this.fields)
    }
  },
  methods: {
    structureOf (fields) {
      return (fields || []).map(field => field.type).join(',')
    },
    parseFields (value) {
      if (!value) {
        return []
      }
      try {
        const parsed = typeof value === 'string' ? JSON.parse(value) : value
        return Array.isArray(parsed) ? parsed : []
      } catch (e) {
        return []
      }
    },
    async fetchForm (version) {
      const response = await axios.get(this.baseDomain + this.shopId + '/forms/' + this.form.id + '.json?version=' + version + '&apikey=' + this.apiKey)
      return response.data
    },
    // A translation keeps its own texts, but the field list, types and mandatory flags always follow the default language
    alignWithDefault (translated, defaults) {
      return defaults.map((field, index) => {
        const own = translated[index] && translated[index].type === field.type ? translated[index] : {}
        const aligned = { ...field }
        for (const key of ['title', 'options', 'text']) {
          if (own[key]) {
            aligned[key] = own[key]
          }
        }
        return aligned
      })
    },
    async loadFields () {
      this.translationAligned = false
      if (!this.form.id) {
        return
      }
      try {
        const form = await this.fetchForm(this.version)
        this.form = { ...this.form, name: form.name }
        let fields = this.parseFields(form.fields)
        if (this.isTranslation) {
          const defaults = this.parseFields((await this.fetchForm(this.defaultVersion)).fields)
          const aligned = this.alignWithDefault(fields, defaults)
          this.translationAligned = this.structureOf(aligned) !== this.structureOf(fields)
          fields = aligned
        }
        this.fields = fields
        this.loadedStructure = this.structureOf(fields)
      } catch (e) {
        console.error(e)
      }
    },
    hideModal () {
      this.$emit('close')
    },
    saveForm () {
      this.form.fields = this.fields
      this.$emit('saveForm', this.form)
    },
    deleteFormDialog () {
      if (!this.form.id) {
        return
      }
      this.showDialog({
        title: this._('Are you sure? Deletion can\'t be undone.'),
        text: this._('The form will be removed from all products. Answers on existing orders will be kept.'),
        width: 800,
        buttons: [
          {
            title: this._('Cancel'),
            handler: () => {}
          },
          {
            title: this._('Delete'),
            class: 'danger',
            icon: 'trash',
            handler: () => {
              this.$emit('deleteForm', this.form)
            }
          }
        ]
      })
    },
    setData (data) {
      this.form.data = data
    },
    addField (type) {
      if (!this.fields) {
        this.fields = []
      }
      this.fields.push({
        title: '',
        type: type
      })
      this.editFieldDialog(this.fields.length - 1)
    },
    editFieldDialog (index) {
      let field = this.fields[index]
      const requiredFields = []
      const fields = []
      if (field.type === 'static' || field.type === 'h3') {
        fields.push({
          title: this._('Text'),
          type: 'textarea',
          name: 'text',
          placeholder: this._('Enter the text to display'),
          defaultValue: field.text,
          localized: true
        })
        requiredFields.push('text')
      } else {
        fields.push({
          title: this._('Field title'),
          type: 'text',
          name: 'title',
          placeholder: this._('Enter the field title'),
          defaultValue: field.title,
          localized: true
        })
        if (!this.isTranslation) {
          fields.push({
            title: this._('Mandatory'),
            type: 'checkbox',
            name: 'mandatory',
            defaultValue: field.mandatory
          })
        }
        requiredFields.push('title')
      }
      if (field.type === 'select') {
        fields.push({
          title: this._('Options'),
          type: 'textarea',
          name: 'options',
          placeholder: this._('Enter field options, one per line'),
          defaultValue: field.options,
          localized: true
        })
        requiredFields.push('options')
      }
      this.showDialog({
        title: field.title || this._('Field'),
        width: 700,
        buttons: [
          {
            title: this._('Cancel'),
            icon: 'close',
            handler: () => {}
          },
          {
            title: this._('Ok'),
            class: 'success',
            icon: 'check',
            requiredFields: requiredFields,
            handler: (data) => {
              this.fields[index] = { ...field, ...data }
            }
          }
        ],
        fields: fields
      })
    },
    removeField (index) {
      this.fields.splice(index, 1)
    },
    moveFieldUp (index) {
      this.fields.splice(index - 1, 0, this.fields.splice(index, 1)[0])
    },
    moveFieldDown (index) {
      this.fields.splice(index + 1, 0, this.fields.splice(index, 1)[0])
    },
    fieldClass (field) {
      return 'form-group'
    },
    fieldLabel (field) {
      return (id) => {
        const o = field.options.find(opt => opt.value === id)
        return o && o.name ? o.name : id
      }
    },
    isInputField (field) {
      if (!field) {
        return false
      }
      switch (field.type) {
        case 'text':
        case 'email':
        case 'number':
        case 'password':
          return this.checkCondition(field)
      }
      return false
    },
    isCheckbox (field) {
      if (!field) {
        return false
      }
      if (field.type === 'checkbox') {
        return this.checkCondition(field)
      }
      return false
    },
    isSelectField (field) {
      if (!field) {
        return false
      }
      if (field.type === 'select' && field.options) {
        return this.checkCondition(field)
      }
      return false
    },
    isStaticText (field) {
      if (!field) {
        return false
      }
      if (field.type === 'static') {
        return this.checkCondition(field)
      }
      return false
    },
    isSeparator (field) {
      if (!field) {
        return false
      }
      if (field.type === 'separator') {
        return this.checkCondition(field)
      }
      return false
    },
    isInfo (field) {
      if (!field) {
        return false
      }
      if (field.type === 'info' && field.text) {
        return this.checkCondition(field)
      }
      return false
    },
    isH3 (field) {
      if (!field) {
        return false
      }
      if (field.type === 'h3' && field.text) {
        return this.checkCondition(field)
      }
      return false
    },
    isH4 (field) {
      if (!field) {
        return false
      }
      if (field.type === 'h4' && field.text) {
        return this.checkCondition(field)
      }
      return false
    },
    isTextArea (field) {
      if (!field) {
        return false
      }
      if (field.editor === 'richEditor') {
        return false
      }
      if (field.type === 'textarea') {
        return this.checkCondition(field)
      }
      return false
    },
    checkCondition(field) {
      if (field.condition && field.conditionValue) {
        if (field.conditionValue.includes && field.conditionValue.includes(this.data[field.condition])) {
          return true
        }
        if (this.data[field.condition] == field.conditionValue) {
          return true
        }
        return false
      }
      return true
    },
    parseOptions(options) {
      return options.split('\n').map(opt => {
        if (opt) {
          return {
            name: opt,
            value: opt
          }
        }
        return null
      }).filter(opt => opt)
    }
  },
  watch: {
    version () {
      this.loadFields()
    }
  },
  mounted () {
    if (this.sourceForm && this.sourceForm.fields) {
      this.fields = this.parseFields(this.sourceForm.fields)
      this.loadedStructure = this.structureOf(this.fields)
    }
    if (this.isTranslation) {
      this.loadFields()
    }
  }
}
</script>